/**
 * Enhanced error handling system for file uploads
 * Provides consistent error formatting and recovery suggestions
 */

import { ValidationErrorCodes, ValidationWarningCodes } from '../services/fileValidation';

/**
 * Error severity levels
 */
export const ErrorSeverity = {
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

/**
 * Recovery action types
 */
export const RecoveryActionType = {
  RETRY: 'retry',
  CONVERT: 'convert',
  RESIZE: 'resize',
  SELECT_DIFFERENT: 'select_different',
  COMPRESS: 'compress',
  CHECK_FILE: 'check_file'
};

/**
 * Error formatting utilities
 */
export class ErrorHandlingSystem {
  /**
   * Formats a validation error with consistent structure
   * @param {Object} error - The error object
   * @returns {Object} Formatted error with enhanced information
   */
  static formatError(error) {
    const baseError = {
      code: error.code,
      message: error.message,
      actionableAdvice: error.actionableAdvice,
      severity: this.getErrorSeverity(error.code),
      recoveryActions: this.getRecoveryActions(error.code),
      userFriendlyMessage: this.getUserFriendlyMessage(error.code, error),
      technicalDetails: error.currentValue ? {
        currentValue: error.currentValue,
        expectedValue: error.expectedValue
      } : null
    };

    return baseError;
  }

  /**
   * Formats multiple errors with priority ordering
   * @param {Array} errors - Array of error objects
   * @returns {Array} Formatted and prioritized errors
   */
  static formatErrors(errors) {
    return errors
      .map(error => this.formatError(error))
      .sort((a, b) => this.getErrorPriority(a.code) - this.getErrorPriority(b.code));
  }

  /**
   * Gets the severity level for an error code
   * @param {string} errorCode - The error code
   * @returns {string} Severity level
   */
  static getErrorSeverity(errorCode) {
    const severityMap = {
      [ValidationErrorCodes.NO_FILE]: ErrorSeverity.ERROR,
      [ValidationErrorCodes.INVALID_FORMAT]: ErrorSeverity.ERROR,
      [ValidationErrorCodes.FILE_TOO_LARGE]: ErrorSeverity.ERROR,
      [ValidationErrorCodes.FILE_TOO_SMALL]: ErrorSeverity.ERROR,
      [ValidationErrorCodes.INVALID_MIME_TYPE]: ErrorSeverity.ERROR,
      [ValidationErrorCodes.NOT_AN_IMAGE]: ErrorSeverity.ERROR,
      [ValidationErrorCodes.VALIDATION_ERROR]: ErrorSeverity.ERROR,
      [ValidationWarningCodes.LOW_RESOLUTION]: ErrorSeverity.WARNING,
      [ValidationWarningCodes.HIGH_RESOLUTION]: ErrorSeverity.INFO
    };

    return severityMap[errorCode] || ErrorSeverity.ERROR;
  }

  /**
   * Gets recovery actions for an error code
   * @param {string} errorCode - The error code
   * @returns {Array} Array of recovery actions
   */
  static getRecoveryActions(errorCode) {
    const actionMap = {
      [ValidationErrorCodes.NO_FILE]: [
        { type: RecoveryActionType.SELECT_DIFFERENT, label: 'Select a file' }
      ],
      [ValidationErrorCodes.INVALID_FORMAT]: [
        { type: RecoveryActionType.CONVERT, label: 'Convert to JPEG, PNG, GIF, or WebP' },
        { type: RecoveryActionType.SELECT_DIFFERENT, label: 'Choose a different file' }
      ],
      [ValidationErrorCodes.FILE_TOO_LARGE]: [
        { type: RecoveryActionType.COMPRESS, label: 'Compress the image' },
        { type: RecoveryActionType.RESIZE, label: 'Reduce image dimensions' },
        { type: RecoveryActionType.SELECT_DIFFERENT, label: 'Choose a smaller file' }
      ],
      [ValidationErrorCodes.FILE_TOO_SMALL]: [
        { type: RecoveryActionType.CHECK_FILE, label: 'Check if file is corrupted' },
        { type: RecoveryActionType.SELECT_DIFFERENT, label: 'Try a different image' }
      ],
      [ValidationErrorCodes.INVALID_MIME_TYPE]: [
        { type: RecoveryActionType.SELECT_DIFFERENT, label: 'Select a valid image file' },
        { type: RecoveryActionType.CONVERT, label: 'Convert to supported format' }
      ],
      [ValidationErrorCodes.NOT_AN_IMAGE]: [
        { type: RecoveryActionType.CHECK_FILE, label: 'Verify file is an image' },
        { type: RecoveryActionType.SELECT_DIFFERENT, label: 'Choose a different file' }
      ],
      [ValidationErrorCodes.VALIDATION_ERROR]: [
        { type: RecoveryActionType.RETRY, label: 'Try again' },
        { type: RecoveryActionType.SELECT_DIFFERENT, label: 'Choose a different file' }
      ],
      [ValidationWarningCodes.LOW_RESOLUTION]: [
        { type: RecoveryActionType.SELECT_DIFFERENT, label: 'Use a higher resolution image' }
      ],
      [ValidationWarningCodes.HIGH_RESOLUTION]: [
        { type: RecoveryActionType.RESIZE, label: 'Image will be optimized automatically' }
      ]
    };

    return actionMap[errorCode] || [
      { type: RecoveryActionType.SELECT_DIFFERENT, label: 'Try a different file' }
    ];
  }

  /**
   * Gets user-friendly message for an error code
   * @param {string} errorCode - The error code
   * @param {Object} error - The original error object
   * @returns {string} User-friendly message
   */
  static getUserFriendlyMessage(errorCode, error) {
    const messageMap = {
      [ValidationErrorCodes.NO_FILE]: 'Please select an image to upload.',
      [ValidationErrorCodes.INVALID_FORMAT]: 'This file format isn\'t supported. Please use JPEG, PNG, GIF, or WebP.',
      [ValidationErrorCodes.FILE_TOO_LARGE]: `This file is too large. Please use an image under 5MB.`,
      [ValidationErrorCodes.FILE_TOO_SMALL]: 'This file appears to be empty or corrupted.',
      [ValidationErrorCodes.INVALID_MIME_TYPE]: 'This doesn\'t appear to be a valid image file.',
      [ValidationErrorCodes.NOT_AN_IMAGE]: 'This file isn\'t a valid image.',
      [ValidationErrorCodes.VALIDATION_ERROR]: 'There was a problem validating your file.',
      [ValidationWarningCodes.LOW_RESOLUTION]: 'This image has low resolution and may not look great.',
      [ValidationWarningCodes.HIGH_RESOLUTION]: 'This image is very large and will be optimized for web display.'
    };

    return messageMap[errorCode] || error.message || 'An unexpected error occurred.';
  }

  /**
   * Gets priority for error ordering (lower number = higher priority)
   * @param {string} errorCode - The error code
   * @returns {number} Priority number
   */
  static getErrorPriority(errorCode) {
    const priorityMap = {
      [ValidationErrorCodes.NO_FILE]: 1,
      [ValidationErrorCodes.INVALID_FORMAT]: 2,
      [ValidationErrorCodes.INVALID_MIME_TYPE]: 3,
      [ValidationErrorCodes.NOT_AN_IMAGE]: 4,
      [ValidationErrorCodes.FILE_TOO_LARGE]: 5,
      [ValidationErrorCodes.FILE_TOO_SMALL]: 6,
      [ValidationErrorCodes.VALIDATION_ERROR]: 7,
      [ValidationWarningCodes.LOW_RESOLUTION]: 8,
      [ValidationWarningCodes.HIGH_RESOLUTION]: 9
    };

    return priorityMap[errorCode] || 10;
  }

  /**
   * Creates a comprehensive error report
   * @param {Array} errors - Array of errors
   * @param {Array} warnings - Array of warnings
   * @returns {Object} Comprehensive error report
   */
  static createErrorReport(errors = [], warnings = []) {
    const formattedErrors = this.formatErrors(errors);
    const formattedWarnings = this.formatErrors(warnings);

    return {
      hasErrors: formattedErrors.length > 0,
      hasWarnings: formattedWarnings.length > 0,
      errorCount: formattedErrors.length,
      warningCount: formattedWarnings.length,
      errors: formattedErrors,
      warnings: formattedWarnings,
      primaryError: formattedErrors[0] || null,
      allRecoveryActions: this.getAllRecoveryActions(formattedErrors),
      summary: this.createErrorSummary(formattedErrors, formattedWarnings)
    };
  }

  /**
   * Gets all unique recovery actions from multiple errors
   * @param {Array} formattedErrors - Array of formatted errors
   * @returns {Array} Unique recovery actions
   */
  static getAllRecoveryActions(formattedErrors) {
    const allActions = formattedErrors.flatMap(error => error.recoveryActions);
    const uniqueActions = allActions.filter((action, index, self) => 
      index === self.findIndex(a => a.type === action.type)
    );
    return uniqueActions;
  }

  /**
   * Creates a summary of errors and warnings
   * @param {Array} errors - Formatted errors
   * @param {Array} warnings - Formatted warnings
   * @returns {string} Error summary
   */
  static createErrorSummary(errors, warnings) {
    if (errors.length === 0 && warnings.length === 0) {
      return 'No issues found.';
    }

    const parts = [];
    
    if (errors.length > 0) {
      parts.push(`${errors.length} error${errors.length > 1 ? 's' : ''}`);
    }
    
    if (warnings.length > 0) {
      parts.push(`${warnings.length} warning${warnings.length > 1 ? 's' : ''}`);
    }

    return `Found ${parts.join(' and ')}.`;
  }

  /**
   * Checks if an error is recoverable
   * @param {string} errorCode - The error code
   * @returns {boolean} Whether the error is recoverable
   */
  static isRecoverable(errorCode) {
    const nonRecoverableErrors = [
      ValidationErrorCodes.VALIDATION_ERROR
    ];
    
    return !nonRecoverableErrors.includes(errorCode);
  }

  /**
   * Gets help text for recovery actions
   * @param {string} actionType - The recovery action type
   * @returns {string} Help text
   */
  static getRecoveryHelpText(actionType) {
    const helpTextMap = {
      [RecoveryActionType.RETRY]: 'Try uploading the same file again.',
      [RecoveryActionType.CONVERT]: 'Use an image editor or online converter to change the file format.',
      [RecoveryActionType.RESIZE]: 'Use an image editor to reduce the image dimensions.',
      [RecoveryActionType.SELECT_DIFFERENT]: 'Choose a different image file from your device.',
      [RecoveryActionType.COMPRESS]: 'Use an image compressor to reduce the file size while maintaining quality.',
      [RecoveryActionType.CHECK_FILE]: 'Make sure the file isn\'t corrupted and is a valid image.'
    };

    return helpTextMap[actionType] || 'Try a different approach to resolve this issue.';
  }
}

export default ErrorHandlingSystem;